/**
 * What the review surface shows for one page, and whether it shows it.
 *
 * These are the review belt's card rules, moved verbatim out of
 * `UrlDetailComponent` so that something other than a browser can ask the same
 * questions — the agent export reads this class on the server and must list
 * exactly what Review lists (AGENT_EXPORT_MCP.md §2a). The component keeps a
 * one-line delegate per method, so its template and specs are unchanged.
 *
 * The class holds no loading logic. Whoever owns the data builds a
 * `ReviewPageSource` and the answers follow from it: the component hands over
 * its own fields, the server hands over the same payloads read from the
 * database. Two places asking these questions with two copies of the rules is
 * the drift this file exists to prevent — change a rule here, not beside it.
 */

import type {
  ActionPlanInternalLinkRow,
  ActionPlanUnclusteredPageRow,
  CompareSummary,
  PageIntentDiagnosis,
  PageSerpResultRow,
  PageSerpSnapshot,
  SerpRivalCoverageData,
  SupportingPagesV2EvalData,
  SupportingPagesV2RowResult
} from './api-types';
import {
  estimateBesideRival,
  type EstimateBesideRivalView,
  type PageEstimatePairSource
} from './estimate-beside-rival';
import {
  comparePageFreshnessToSerp,
  FRESHNESS_SERP_MIN_DATED_RESULTS,
  formatFreshnessDateLabel,
  freshnessDiagnosisLabel as resolveFreshnessDiagnosisLabel,
  freshnessDiagnosisTone as resolveFreshnessDiagnosisTone,
  medianPublishedDate,
  pageContentLastModifiedLabel,
  parsePageContentLastModifiedDate,
  serpWinnerPublishedDatesFromResults
} from './freshness-serp-compare';
import type { InventoryPageSignal, InventoryPageSignalKind } from './inventory-page-signals';
import {
  filterInternalLinksPendingIssuesTriage,
  internalLinkSuggestionKeysInWorkItems
} from './internal-link-triage';
import { missingSectionsCountLabel } from './missing-sections-summary';
import {
  effectiveSectionCompareStatus,
  sectionCompareNotice,
  type SectionCompareNotice
} from './section-compare-unavailable';
import {
  serpOtherPageNotice,
  serpResultsShowAnotherPage,
  serpSubjectWithheldNotice,
  type SerpOtherPageNotice,
  type SerpSubjectWithheldKind,
  type SerpSubjectWithheldNotice
} from './serp-subject-withheld';
import type { EvidenceTone } from './page-evidence';
import {
  mixedSerpAlignment,
  pageTypeAdviceWithheldByRank,
  pageTypeFirstChange,
  pageTypeWholePageVerdict,
  serpPageTypeExamples,
  type PageTypeWholePageVerdict,
  type PageTypeVerdictRoute,
  type MixedSerpAlignment,
  type PageTypeExample
} from './page-type-alignment';
import {
  buildPlanV2IssueKey,
  type PlanV2IssueDispositionRecord,
  type PlanV2IssueType
} from './plan-v2-issues';
import { rankDrivenGapsForV2Row, type RankDrivenSectionGap } from './rank-driven-section-gaps';
import { reviewAnswerVocabGroups, type ReviewAnswerVocab } from './review-answer-vocab';
import {
  reviewMissingSections,
  splitSectionGapsByOwnPage,
  type ReviewAlreadyOnPageSection,
  type ReviewMissingSectionsView
} from './review-missing-sections';
import { ownPagePassages, type OwnPagePassage } from './already-on-page';
import { RANK_DRIVEN_GAPS_ENABLED, reviewRankGaps, type ReviewRankGapsView } from './review-rank-gaps';
import {
  hasReviewSupportingPagesGaps,
  reviewNewPageIdeasAlreadyOnPage,
  reviewSupportingPagesColumns as buildReviewSupportingPagesColumns,
  type ReviewNewPageIdeaOnPage,
  type ReviewSupportingPagesColumn
} from './review-supporting-pages';
import { sectionGapsForV2Row } from './section-gaps';
import { foldLexicalToken } from './serp-intent-highlight';
import { findTopSerpRival } from './serp-rival';
import {
  buildSerpFoldedTermSegments,
  buildSerpSnippetSegments,
  type SerpSnippetSegment
} from './serp-snippet-segments';
import { dedupeOverlappingStructurePassages } from './serp-structure-passage';
import { decidedSiteWideCodes, settledSiteWideLabel, splitSettledSiteWide } from './site-wide-suppression';
import {
  supportingPagesColumnsReady,
  supportingPagesSettling,
  type SupportingPagesGateState,
  type SupportingPagesLoadStatus
} from './supporting-pages-render-gate';
import {
  attachV2RowsToSupportingPageColumns,
  augmentSupportingPagesColumns,
  buildSupportingPagesUnifiedColumns,
  buildSupportingPagesV2OnlyColumns,
  hasListInventoryGaps,
  hasMissingSectionGaps,
  hasRankDrivenGaps,
  type SerpCompetitorColumnSeed,
  type SupportingPagesUnifiedColumn
} from './supporting-pages-unified-columns';
import { reviewTechnicalChecks, storedHtmlScopesCheckToPage } from './review-technical-checks';
import { technicalReviewIssues, technicalScopeLabel } from './technical-evidence';
import {
  filterRowsByFocalAnchorTopics,
  formatSupportingPagePath,
  formatSupportingPageTraffic,
  normalizeUrlForCompare,
  type DomainTopicUrlRow
} from './topic-supporting-pages';
import { buildOnpageTechnicalIssueViews, type OnpageIssueView } from './url-detail-onpage-issues';
import {
  unclusteredEvidenceSentenceFor,
  unclusteredRecommendationLabel
} from './page-traffic-evidence';
import { unclusteredClusterAction, unclusteredClusterFitSentence } from './unclustered-cluster-fit';
import {
  cannibalizationCheckedForPage,
  cannibalizationSetAsideForPage,
  cannibalizationPairView,
  cannibalizationVerdictsForPage,
  competingPairVerdicts,
  type CannibalizationCheckedPair,
  type CannibalizationSetAsidePair,
  type CannibalizationPairView,
  type CompetingPagesPayload,
  type CompetingPairVerdict
} from './cannibalization-card';
import { nearMissCard, type NearMissCardView, type NearMissCheckedLine } from './near-miss-card';
import type { NearMissPageResult } from './near-miss-searches';

/** A card's verdict tone. Aliased onto `EvidenceTone`, as the component always did. */
export type DiagnosisTone = EvidenceTone;

/** One domain's block of the Supporting pages snapshot, after the entity filter. */
export interface TopicOwnershipDomain {
  domain: string;
  isYourSite: boolean;
  rows: DomainTopicUrlRow[];
}

export type ReviewLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Everything the card rules read. Field names are the component's own, so the
 * move stayed mechanical; the server fills the same fields from the database.
 */
export interface ReviewPageSource {
  readonly url: string | null;
  readonly pageUrlId: string;

  /** `'planV2'` when the page is embedded in Issues or Review. */
  readonly embedPreset: string | null;
  readonly embedActionsOnly: boolean;
  readonly embedReviewSurface: boolean;
  readonly embedCompactPeek: boolean;

  /** The `/urls/analysis` payload's `data`, as URL Detail stores it. */
  readonly analysisData: any;
  readonly analysisStatus: 'idle' | 'loading' | 'success' | 'error';
  readonly pageDemand: { claim?: { label?: string | null } | null } | null;
  readonly pageSerp: PageSerpSnapshot | null;
  readonly pageIntentDiagnosis: PageIntentDiagnosis | null;
  readonly pageCompare: CompareSummary | null;

  readonly markedCompetitorDomains: string[];
  readonly focalAnchorTopicSlugs: Set<string>;
  readonly topicOwnershipStatus: ReviewLoadStatus;
  readonly topicOwnershipDomains: TopicOwnershipDomain[];
  readonly topicOwnershipV2Status: ReviewLoadStatus;
  readonly topicOwnershipV2Data: SupportingPagesV2EvalData | null;
  /** Rows from the live opportunity eval of marked rivals on this page's SERP. */
  readonly markedSerpCompetitorV2Rows: SupportingPagesV2RowResult[];
  /** That eval is still running. */
  readonly markedSerpEvalPending: boolean;
  readonly serpRivalCoverage: SerpRivalCoverageData | null;

  /** The page's plan cluster labels, from the analysis payload. */
  readonly planClusters: string[];
  readonly pageSignals: InventoryPageSignal[];
  readonly pageSignalsLoading: boolean;
  readonly internalLinkSignalRows: ActionPlanInternalLinkRow[];
  readonly unclusteredSignalRows: ActionPlanUnclusteredPageRow[];
  /**
   * Which pages of the site compete, for the Cannibalization card (`GET /urls/competing-pages`,
   * once per domain). Review surface only; null or absent before it answers, when it failed, or
   * off review.
   */
  readonly competingPages?: CompetingPagesPayload | null;
  /**
   * The searches this page is close on, for the "You're close on these searches" card
   * (`GET /urls/near-miss-searches`, per page; quality sprint 8). Review surface only; null or
   * absent before it answers, when it failed, or off review.
   */
  readonly nearMissSearches?: NearMissPageResult | null;
  /**
   * This page's DataForSEO estimate and the compare target's, each tagged with the formula it was
   * made under, and this page's Search Console clicks where it is on (`GET /urls/analysis`,
   * `pageEstimatePair`; TRAFFIC_ESTIMATE_CUTOVER.md D3). Null or absent when either has no estimate.
   */
  readonly pageEstimatePair?: PageEstimatePairSource | null;

  /** The domain's template-level check codes; null before the rollup answers. */
  readonly siteWideIssueCodes: Set<string> | null;
  /**
   * Every URL our site has, for the new-page guard (a rival page we already
   * have is not a page to write). Null until it lands: the review card waits
   * for it rather than listing a page we have and then taking it back.
   */
  readonly ownSiteUrls: readonly string[] | null;
  readonly sharedIssueCauses: Record<string, string>;

  /** This period's decisions, keyed by issue key — the Issues queue's records. */
  readonly issueRecords: Map<string, PlanV2IssueDispositionRecord>;
  /** Not yet sent or put aside — `PlanV2IssueQueueService.isVisibleInIssuesEmbed`. */
  isIssueVisible(issueKey: string): boolean;
}

/** One technical finding as the review card lists it. */
export interface ReviewTechnicalFinding {
  code: string;
  title: string;
  /** "This page" or "Site-wide"; null before the domain rollup answers. */
  scope: string | null;
}

/**
 * What the review belt shows for one page, card by card, as data.
 *
 * Each field is null exactly when the belt does not render that card, so a
 * reader that walks these fields lists what Review lists and nothing else. The
 * belt itself still renders through the component's delegates; this exists for
 * readers that are not a browser — the agent export.
 */
export interface ReviewPageCards {
  supportingPages: ReviewSupportingPagesColumn[] | null;
  sections: {
    title: string;
    missing: ReviewMissingSectionsView | null;
    rank: ReviewRankGapsView | null;
  } | null;
  /** Why the section comparison is blank, when it is blank for a reason (§0at). */
  sectionCompareNotice: SectionCompareNotice | null;
  /**
   * "Add X" items left off because our page already has X, each with the line of ours that shows
   * it (TECHNICAL_DEBT §0cb, §0ce). Null when there are none.
   */
  alreadyOnPage: { sections: ReviewAlreadyOnPageSection[]; newPages: ReviewNewPageIdeaOnPage[] } | null;
  /**
   * The whole comparison with Google's results is held back, because they are about something
   * else (§0ar). When set, sections, freshness, answer structure and page type are all null.
   */
  comparisonWithheld: SerpSubjectWithheldNotice | null;
  /**
   * The comparison is held back because Google's results show another page of the site, not this
   * one (quality sprint 7 C). The same cards go as with `comparisonWithheld`, but `/review` shows no
   * card for it: the pair's Cannibalization card covers competing pages, and otherwise the fault is
   * our pick of the search, with nothing for the writer to do. The everything export lists it.
   */
  comparisonOnAnotherPage: SerpOtherPageNotice | null;
  freshness: {
    label: string;
    tone: DiagnosisTone;
    yourPage: string;
    serpTypical: { count: number; typicalLabel: string } | null;
    verdict: string | null;
  } | null;
  answerStructure: {
    instruction: string | null;
    vocab: ReviewAnswerVocab | null;
    yours: string | null;
    theirs: Array<{ position: number; domain: string; text: string }>;
  } | null;
  pageType: {
    chipLabel: string;
    serpFamily: string;
    yourFamily: string;
    briefLines: string[];
    examplesLabel: string;
    examples: PageTypeExample[];
  } | null;
  technicalIssues: ReviewTechnicalFinding[] | null;
  technicalWarnings: ReviewTechnicalFinding[] | null;
  internalLinks: ActionPlanInternalLinkRow[] | null;
  /**
   * The Cannibalization card: this page's competing pairs with a merge or differentiate verdict,
   * each naming the other page and the shared searches (`cannibalization-card.ts`). Replaces the
   * Signals card's bare "Open pair with" line on the review surface.
   */
  cannibalization: { pairs: CannibalizationPairView[] } | null;
  /**
   * "You're close on these searches": the searches this page shows at positions 4-20 with a change
   * to make (wording, a section, the steps), most demand first (`near-miss-card.ts`). Null when no
   * search has one; those go to the everything export as "Checked" lines.
   */
  nearMissSearches: NearMissCardView | null;
  signals: {
    labels: string[];
    planClusters: string[];
    unclustered: {
      recommendation: string;
      label: string;
      evidence: string | null;
      /** Which cluster its siblings point to, or why none fits (null on "Delete"). */
      clusterFit: string | null;
      topTopics: string[];
    } | null;
    noPlanCluster: boolean;
  } | null;
  /**
   * This page's traffic estimate beside the rival page's on its comparison search, only when both
   * were made the same way, with this page's real clicks where Search Console is on
   * (`estimate-beside-rival.ts`; owner, 2026-11-03). Context, not a finding: no actions.
   */
  estimateBesideRival: EstimateBesideRivalView | null;
}

export class ReviewPageModel {
  constructor(private readonly src: ReviewPageSource) {}

  private passagesFor: unknown = undefined;
  private passages: OwnPagePassage[] = [];

  /** Our page's stored headings and sentences, read once per analysis payload. */
  ownPagePassages(): OwnPagePassage[] {
    if (this.passagesFor !== this.src.analysisData) {
      this.passagesFor = this.src.analysisData;
      this.passages = ownPagePassages(this.src.analysisData);
    }
    return this.passages;
  }

  isPlanV2Embed(): boolean {
    return this.src.embedPreset === 'planV2';
  }

  isCompactPeek(): boolean {
    return this.isPlanV2Embed() && this.src.embedCompactPeek;
  }

  embedActionsOnlyActive(): boolean {
    return this.isPlanV2Embed() && (this.src.embedActionsOnly || this.src.embedCompactPeek);
  }

  embedDiagnosisIsGreen(tone: DiagnosisTone): boolean {
    return tone === 'ok';
  }

  showEmbedSupportingPagesCard(): boolean {
    let base = true;
    // Review: this card is now only about pages to write, so a page whose only
    // finding is a missing section must not render it — the Sections card below
    // is where that page's work is, and an empty "Supporting pages" beside it is
    // a card whose whole content is the word "Nothing".
    if (this.src.embedReviewSurface && this.supportingPagesColumnsReady()) {
      return this.embedIssueVisible(
        this.planV2IssueKey('topic_ownership'),
        this.hasReviewSupportingPagesGaps()
      );
    }
    if (this.isPlanV2Embed()) {
      if (!this.embedActionsOnlyActive()) base = true;
      else if (this.src.topicOwnershipStatus === 'loading' || this.src.topicOwnershipV2Status === 'loading') {
        base = true;
      } else if (this.src.topicOwnershipV2Status === 'ready' && (this.src.topicOwnershipV2Data?.rows?.length ?? 0) > 0) {
        base = this.hasSupportingPagesActionableGap();
      } else if (this.src.topicOwnershipStatus !== 'ready' || !this.src.topicOwnershipDomains.length) {
        base = false;
      } else {
        base = this.hasSupportingPagesActionableGap();
      }
    }
    return this.embedIssueVisible(this.planV2IssueKey('topic_ownership'), base);
  }

  showEmbedFreshnessSection(): boolean {
    if (this.comparisonHeldBack()) return false;
    if (!this.showContentFreshnessCard()) return false;
    // An undated page has nothing to set beside the rivals' dates, so the item could only say "compare
    // when you have a date" (TECHNICAL_DEBT §0ch). The crawl-run history can't stand in: a page's
    // content fingerprint changes with every site-wide template change, so it dates the template, not an edit.
    // URL detail keeps the card, so the rivals' dates stay one click away.
    if (this.src.embedReviewSurface && !this.pageContentLastModifiedDate()) return false;
    // The same holds the other way round: a dated page whose results carry too few dates has nothing
    // to be set beside either. The item would rest on the page's age alone, or call one result's date
    // typical without showing it. That isn't a failed measurement (the results carry no dates), so no
    // "can't tell" card replaces it. URL detail keeps the card.
    if (this.src.embedReviewSurface && !this.serpWinnersPublishedDateSummary()) return false;
    let base = true;
    if (this.embedActionsOnlyActive()) {
      base = !this.embedDiagnosisIsGreen(this.freshnessDiagnosisTone());
    }
    return this.embedIssueVisible(this.planV2IssueKey('freshness'), base);
  }

  showEmbedSerpPhraseCard(): boolean {
    if (this.comparisonHeldBack()) return false;
    // Nothing to compare against is not a decision.
    //
    // The Dashboard already files this under Clear — "the top results share no
    // vocabulary, so there is nothing to match against" — while the belt was
    // still asking the operator to REVIEW STRUCTURE against rivals it could
    // not show. Inventory and URL detail keep the card: reading why a SERP is
    // fragmented is exactly the kind of thing those screens are for.
    // A tool page on such a search can still be told the app's own route, when the
    // results agree on it (§0cb): that is the comparison, made from the results.
    if (
      this.src.embedReviewSurface &&
      this.serpAnswerStructureIsFragmented() &&
      !this.hasLexicalVocabChips() &&
      !this.src.pageIntentDiagnosis?.answerStructureInAppRoute
    ) {
      return false;
    }
    // The results give the app's tab and no step after it, over a page whose only steps
    // are the tool's: a bare tab was graded unfinished and "your page matches" wrong (§0cb).
    if (this.src.embedReviewSurface && this.src.pageIntentDiagnosis?.answerStructureInAppRouteWithheld) {
      return false;
    }
    // The page-type verdict on this page names the steps itself.
    if (this.pageTypeVerdictFoldsSteps()) return false;
        // Doing it right, and missing nothing, is not a decision either.
    //
    // The belt exists to hand off work. A card that says "you match" is a
    // congratulation, and the operator's rule for this surface is that a card
    // with nothing to send should not be there: *"If you're already doing it
    // right and not missing anything in the answer structure, then nothing
    // should be shown."* The vocabulary half is kept separate on purpose — a
    // page can deliver the right shape and still never use the words the SERP
    // uses, and that IS a work item. So this hides the card only when both
    // halves are clear.
    if (this.src.embedReviewSurface && this.reviewAnswerStructureHasNothingToSend()) {
      return false;
    }
    let base = this.showSerpPhraseMatchCard();
    if (base && this.embedActionsOnlyActive()) {
      base = !this.embedDiagnosisIsGreen(this.serpPhraseMatchActionChipTone());
    }
    return this.embedIssueVisible(this.planV2IssueKey('serp_phrase'), base);
  }

  showEmbedSerpIntentCard(): boolean {
    if (this.comparisonHeldBack()) return false;
    let base = this.hasPageIntentDiagnosis();
    if (base && this.embedActionsOnlyActive()) {
      base = !this.embedDiagnosisIsGreen(this.pageIntentMatchActionChipTone());
    }
    // A card that asks for a page-type change must say which change, or not be there.
    if (base && this.reviewPageTypeAsksForChange() && !this.reviewPageTypeAdvice()) return false;
    return this.embedIssueVisible(this.planV2IssueKey('serp_intent'), base);
  }

  showEmbedTechnicalIssuesCard(): boolean {
    if (this.isCompactPeek()) return false;
    const base = !this.embedActionsOnlyActive() || this.hasTechnicalCriticalIssues();
    return this.embedIssueVisible(this.planV2IssueKey('technical_critical'), base);
  }

  showEmbedTechnicalWarningsCard(): boolean {
    if (this.isCompactPeek()) return false;
    const base = !this.embedActionsOnlyActive() || this.hasTechnicalWarnings();
    return this.embedIssueVisible(this.planV2IssueKey('technical_warnings'), base);
  }

  showEmbedSignalsSection(): boolean {
    let base = true;
    if (this.isPlanV2Embed() && this.embedActionsOnlyActive()) {
      if (this.src.pageSignalsLoading) base = true;
      else base = this.hasNonInternalLinkSignals();
    }
    return this.embedIssueVisible(this.planV2IssueKey('signals'), base);
  }

  /**
   * The comparison with Google's results, held back on this page (§0ar), or null.
   *
   * One gate for every card built from those results — sections, freshness, answer structure with
   * its vocabulary, page type — so they go together or not at all, and the belt, the export and
   * the MCP agree because they all read the cards through here. Review surface only: Inventory and
   * URL detail show the results table beside the cards, where an operator can see the mismatch.
   * The query itself is never changed (`resolve-serp-query.ts` is out of bounds for this).
   */
  reviewComparisonWithheld(): SerpSubjectWithheldNotice | null {
    if (!this.comparisonHeldBack()) return null;
    // Another page in the results is the bigger reason, and it shows no card (`comparisonOnAnotherPage`).
    if (serpResultsShowAnotherPage(this.src.pageSerp)) return null;
    return serpSubjectWithheldNotice(this.src.pageSerp, this.withheldKinds());
  }

  /**
   * The comparison held back because the results show another page of the site and not this one,
   * or null. Not a belt card: see `ReviewPageCards.comparisonOnAnotherPage`.
   */
  reviewComparisonOnAnotherPage(): SerpOtherPageNotice | null {
    if (!this.comparisonHeldBack() || !serpResultsShowAnotherPage(this.src.pageSerp)) return null;
    return serpOtherPageNotice(this.src.pageSerp, this.withheldKinds());
  }

  /**
   * This page's estimate beside the rival page's, or null (`estimate-beside-rival.ts`). Not while
   * the comparison is held back: the results are about something else, or show another page of the
   * site, so the page they name is not this page's rival. Only for the page the comparison names
   * now: a pair loaded for another target says nothing about this one.
   */
  reviewEstimateBesideRival(): EstimateBesideRivalView | null {
    const pair = this.src.pageEstimatePair;
    if (!pair || this.comparisonHeldBack()) return null;
    const target = this.src.pageCompare?.target?.url;
    if (!target || !sameUrl(target, pair.rival.url)) return null;
    return estimateBesideRival(pair);
  }

  /**
   * The gate itself: the review surface, and results the server scored as about something else, or
   * results that show another page of the site where this one should be (quality sprint 7 C: the
   * page would be judged on that page's rank and rivals).
   */
  comparisonHeldBack(): boolean {
    if (this.withholdBypassed) return false;
    return (
      this.src.embedReviewSurface &&
      (!!this.src.pageSerp?.subjectMatch?.mismatch || serpResultsShowAnotherPage(this.src.pageSerp))
    );
  }

  /**
   * What this page would have shown had the comparison not been held back, asked of the same card
   * rules with the gate lifted — so the reason names exactly what went, and nothing that was never
   * there. Card order, as the belt lays them out.
   */
  withheldKinds(): SerpSubjectWithheldKind[] {
    this.withholdBypassed = true;
    try {
      const kinds: SerpSubjectWithheldKind[] = [];
      const sections = this.reviewSectionGapContent();
      if (sections && this.src.isIssueVisible(this.planV2IssueKey('section_gap'))) {
        kinds.push(this.reviewGapsCardTitle() === 'Thin sections' ? 'thin_sections' : 'missing_sections');
      }
      if (this.showEmbedFreshnessSection()) kinds.push('freshness');
      if (this.showEmbedSerpPhraseCard() && this.reviewAnswerStructure()) kinds.push('answer_structure');
      if (this.src.pageIntentDiagnosis && this.showEmbedSerpIntentCard()) kinds.push('page_type');
      return kinds;
    } finally {
      this.withholdBypassed = false;
    }
  }

  private withholdBypassed = false;

  planV2IssueKey(issueType: PlanV2IssueType): string {
    return buildPlanV2IssueKey((this.src.pageUrlId || '').trim(), issueType);
  }

  embedIssueVisible(issueKey: string, baseVisible: boolean): boolean {
    if (!baseVisible) return false;
    if (!this.isPlanV2Embed()) return baseVisible;
    return this.src.isIssueVisible(issueKey);
  }

  /**
   * "Answer structure", as the review surface shows it.
   *
   * The deep-dive card answers several questions at once — how the SERP
   * delivers, how this page delivers, which shared vocabulary sits where, what
   * your two strongest passages say, and what nine rivals opened with. That is
   * right on Inventory, where an operator is working out what is going on.
   *
   * Here they are deciding whether to send it, so the card is reduced to the
   * three things that decision needs: what to do, the words the page never
   * uses, and what "doing it" looks like in the rivals' own words beside what
   * yours currently says.
   *
   * Two rules, stated because they are what stops this growing back:
   *
   * **Only the FIRST brief line.** The second is context about vocabulary,
   * which the missing-terms line says in fewer words.
   *
   * **Only excerpts carrying structure evidence, capped at two.** Highlighted
   * means the analyser marked how that winner delivers its answer. An unmarked
   * excerpt is a paragraph, not evidence, and a card that pads itself to look
   * complete is one an operator learns to skim. A page whose rivals carry no
   * marked evidence keeps the instruction and shows no excerpts.
   */
  reviewAnswerStructure(): {
    instruction: string | null;
    vocab: ReviewAnswerVocab | null;
    yours: string | null;
    theirs: Array<{ position: number; domain: string; segments: SerpSnippetSegment[] }>;
  } | null {
    if (!this.src.embedReviewSurface) return null;
    const d = this.src.pageIntentDiagnosis;
    if (!d) return null;

    const instruction = this.answerStructureBriefLines()[0] ?? null;

    // The three tones, not the red one alone — see `reviewAnswerVocabGroups`.
    const vocab = reviewAnswerVocabGroups(this.serpVocabChipRows());

    // The structure passages, then the same fallbacks the deep-dive column uses.
    // Those passages are empty exactly when this page matches none of the SERP's
    // shapes — which is the case the comparison exists for. Without the fallback
    // the card silently drops "Yours opens" on every page worth reading it on.
    const passages = this.searchIntentStructurePagePassages();
    const chosen = passages.find((p) => p.matchKind === 'closest') ?? passages[0] ?? null;
    const yoursText =
      chosen?.text ||
      d.yourBestSection?.text ||
      d.yourIntroSection?.text ||
      '';
    // Deliberately unmarked, unlike theirs. The marks available for your own
    // passage are vocabulary matches — words the SERP shares that you happen to
    // use — and this card is about STRUCTURE. Highlighting "Sheets" and "chart"
    // in your own opening says nothing about how that opening is built, and it
    // reads as if those words were the finding. The rivals' marks stay, because
    // there they are the finding: the span that shows how the winner delivers.
    const yours = yoursText.trim() ? this.reviewTrimPlain(yoursText) : null;

    // When the line names the app's own route, the evidence is the results that
    // give it, with the route marked: sprint 4's grader found the card naming one
    // route while quoting two results that never gave it (§0cb).
    const routeSources = (d.answerStructureInAppRoute?.sources || []).filter((s) => s.snippet && s.quote);
    if (routeSources.length) {
      const theirs = routeSources.slice(0, 2).map((s) => ({
        position: s.position,
        domain: s.domain,
        segments: this.reviewTrimSegments(buildSerpSnippetSegments(s.snippet || '', [s.quote]))
      }));
      return { instruction, vocab, yours, theirs };
    }

    // Segments, not plain text. The mark IS the finding: it is the span the
    // analyser identified as the winner's answer structure, so a quote without
    // it asks the operator to spot the pattern themselves — which is the work
    // this card is supposed to have already done.
    const theirs = this.searchIntentWinnerExcerptsAll()
      .filter((e) => (e.structureEvidenceSpans?.length || 0) > 0)
      .slice(0, 2)
      .map((e) => ({
        position: e.position,
        domain: e.domain,
        segments: this.reviewTrimSegments(
          this.searchIntentExcerptSegments({
            excerpt: e.excerpt,
            structureEvidenceSpans: e.structureEvidenceSpans
          })
        )
      }));

    if (!instruction && !vocab && !theirs.length) return null;
    return { instruction, vocab, yours, theirs };
  }

  /** Your own opening, as plain prose. See the note in `reviewAnswerStructure`. */
  reviewTrimPlain(text: string, max = 190): string {
    const clean = (text || '').replace(/\s+/g, ' ').trim();
    if (clean.length <= max) return clean;
    const cut = clean.slice(0, max);
    const lastSpace = cut.lastIndexOf(' ');
    return `${(lastSpace > 60 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:\s]+$/, '')}…`;
  }

  /**
   * One line of prose on a belt, with its highlights intact.
   *
   * Trimming segments rather than the raw string, because trimming the string
   * first would throw the marks away and re-finding them is a second answer to
   * "where does the evidence start". A highlighted segment is never cut: the
   * mark is the finding, and half a finding is worse than one line more.
   */
  reviewTrimSegments(
    segments: SerpSnippetSegment[],
    max = 190
  ): SerpSnippetSegment[] {
    const out: SerpSnippetSegment[] = [];
    let used = 0;
    for (const seg of segments) {
      const text = (seg.text || '').replace(/\s+/g, ' ');
      if (!text) continue;
      if (used + text.length <= max) {
        out.push({ ...seg, text });
        used += text.length;
        continue;
      }
      if (seg.emphasized) {
        out.push({ ...seg, text });
        used += text.length;
        break;
      }
      const room = Math.max(0, max - used);
      const cut = text.slice(0, room);
      const lastSpace = cut.lastIndexOf(' ');
      const kept = (lastSpace > 20 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:\s]+$/, '');
      if (kept) out.push({ ...seg, text: `${kept}…` });
      else if (out.length) out[out.length - 1] = { ...out[out.length - 1], text: `${out[out.length - 1].text}…` };
      break;
    }
    return out;
  }

  /**
   * What this card should be CALLED, given what is actually in it.
   *
   * "Missing sections" is right for 90% of the cards in the pilot, where every
   * row is a section the page does not have. On the other 10% — 45 pages —
   * every row reads "Shallow coverage — closest: … (78%)", which is the exact
   * opposite claim: you have the section, it is thin. Titling that "Missing
   * sections" tells an operator to write something they already wrote.
   *
   * Decided from the rows rather than from which analyser produced them: the
   * deepen rows all come from rank-driven gaps today, but that is an accident
   * of the section analyser currently clearing `absentTerms` on covered rows,
   * not a rule to encode here.
   */
  reviewGapsCardTitle(): string {
    const sections = this.reviewMissingSections()?.rows ?? [];
    const ranks = this.reviewRankGaps()?.rows ?? [];
    if (!sections.length && !ranks.length) return 'Missing sections';

    const anyToWrite =
      sections.some((row) => row.kind === 'write') || ranks.some((row) => row.isMissing);
    return anyToWrite ? 'Missing sections' : 'Thin sections';
  }

  reviewMissingSectionsChipLabel(): string | null {
    if (!this.src.embedReviewSurface) return null;
    const sections = this.reviewMissingSections()?.rows.length ?? 0;
    const queries = this.reviewRankGaps()?.rows.length ?? 0;

    // Shared with the Dashboard peek, so the two cannot count it differently.
    return missingSectionsCountLabel(sections, queries);
  }

  hasNonInternalLinkSignals(): boolean {
    return this.reviewPageSignals().some((s) => s.kind !== 'internal_link');
  }

  /**
   * The page's signal chips. There is no pair chip (the retired engine's "Cannibal" chip went with
   * it, quality sprint 7 F): the Cannibalization card says which pages compete.
   */
  reviewPageSignals(): InventoryPageSignal[] {
    return this.src.pageSignals;
  }

  private verdictsFor: unknown = undefined;
  private verdicts: CompetingPairVerdict[] = [];

  /** Every competing pair's verdict for the domain, decided once per payload. */
  cannibalizationVerdicts(): CompetingPairVerdict[] {
    const payload = this.src.competingPages ?? null;
    if (this.verdictsFor !== payload) {
      this.verdictsFor = payload;
      this.verdicts = payload ? competingPairVerdicts(payload.pairs, payload.texts) : [];
    }
    return this.verdicts;
  }

  /**
   * The Cannibalization card's pairs: every competing pair this page is in, with its verdict,
   * merge first. Review surface only (`competingPages` is loaded there). A pair that does not
   * compete ("keep both") has no card (owner, 2026-10-19); pairs with a page whose job is not
   * search never reach here (the engine excludes them, `not_a_search_page`).
   */
  reviewCannibalizationPairs(): CannibalizationPairView[] {
    const payload = this.src.competingPages;
    const urlId = (this.src.pageUrlId || '').trim();
    if (!this.src.embedReviewSurface || !payload || !urlId) return [];
    return cannibalizationVerdictsForPage(this.cannibalizationVerdicts(), urlId).map((v) =>
      cannibalizationPairView(v, urlId, payload)
    );
  }

  showEmbedCannibalizationSection(): boolean {
    return this.embedIssueVisible(this.planV2IssueKey('cannibalization'), this.reviewCannibalizationPairs().length > 0);
  }

  /**
   * The pairs this page was checked against and found fine (keep both): no card, the everything
   * export only, so "no card" reads as "checked", not "not looked at".
   */
  reviewCannibalizationChecked(): CannibalizationCheckedPair[] {
    const urlId = (this.src.pageUrlId || '').trim();
    if (!this.src.embedReviewSurface || !urlId) return [];
    return cannibalizationCheckedForPage(this.src.competingPages ?? null, urlId);
  }

  /**
   * This page's competing pairs that get no card (merged elsewhere, one page not ranking, no change
   * named), each with why: the everything export lists them as checked.
   */
  reviewCannibalizationSetAside(): CannibalizationSetAsidePair[] {
    const payload = this.src.competingPages;
    const urlId = (this.src.pageUrlId || '').trim();
    if (!this.src.embedReviewSurface || !payload || !urlId) return [];
    return cannibalizationSetAsideForPage(this.cannibalizationVerdicts(), urlId, payload);
  }

  private nearMissFor: unknown = undefined;
  private nearMiss: ReturnType<typeof nearMissCard> | null = null;

  /** The page's near-miss searches, answered once per payload; null off the review surface or before it loads. */
  private nearMissAnswered(): ReturnType<typeof nearMissCard> | null {
    const result = this.src.embedReviewSurface ? this.src.nearMissSearches ?? null : null;
    if (this.nearMissFor !== result) {
      this.nearMissFor = result;
      this.nearMiss = result ? nearMissCard(result) : null;
    }
    return this.nearMiss;
  }

  /** "You're close on these searches": null when no search has a change to make. */
  reviewNearMissCard(): NearMissCardView | null {
    const answered = this.nearMissAnswered();
    return answered && answered.view.items.length ? answered.view : null;
  }

  showEmbedNearMissSection(): boolean {
    return this.embedIssueVisible(this.planV2IssueKey('near_miss_searches'), this.reviewNearMissCard() !== null);
  }

  /** The near-miss searches with no change to make, each with why: the everything export only. */
  reviewNearMissChecked(): NearMissCheckedLine[] {
    return this.nearMissAnswered()?.checked ?? [];
  }

  /** The page's near-miss searches rest on estimates (no Search Console for the domain). */
  reviewNearMissEstimated(): boolean {
    return !!this.nearMissAnswered()?.view.estimated;
  }

  pageSerpTargetQueryLabel(): string {
    return this.src.pageSerp?.queryDisplay?.trim() || '—';
  }

  pageDemandClaimLabel(): string {
    return this.src.pageDemand?.claim?.label?.trim() || '—';
  }

  pageContentLastModifiedDate(): Date | null {
    return parsePageContentLastModifiedDate(this.src.analysisData?.lastContentModified);
  }

  pageContentLastModifiedLabel(): string {
    return pageContentLastModifiedLabel(
      this.pageContentLastModifiedDate(),
      this.src.analysisData?.lastContentModified
    );
  }

  serpWinnerPublishedDates(): Date[] {
    return serpWinnerPublishedDatesFromResults(this.src.pageSerp?.results);
  }

  serpWinnersPublishedDateSummary(): { count: number; typicalLabel: string } | null {
    const dates = this.serpWinnerPublishedDates().sort((a, b) => a.getTime() - b.getTime());
    if (dates.length < FRESHNESS_SERP_MIN_DATED_RESULTS) return null;
    const median = medianPublishedDate(dates);
    if (!median) return null;
    return {
      count: dates.length,
      typicalLabel: `Typical ${formatFreshnessDateLabel(median)}`
    };
  }

  showContentFreshnessCard(): boolean {
    if (this.src.analysisStatus !== 'success' || !this.src.analysisData) return false;
    return Boolean(this.pageContentLastModifiedDate() || this.serpWinnersPublishedDateSummary());
  }

  freshnessDiagnosisLabel(): string {
    return resolveFreshnessDiagnosisLabel(
      this.pageContentLastModifiedDate(),
      this.serpWinnerPublishedDates()
    );
  }

  freshnessDiagnosisTone(): DiagnosisTone {
    return resolveFreshnessDiagnosisTone(this.freshnessDiagnosisLabel());
  }

  /**
   * Whether a marked competitor has a supporting page this site has no match for.
   *
   * Read from the unified columns, not from the raw domain blocks. The blocks
   * include each domain's HERO — its main competing page, the one the peer
   * comparison already runs against — and counting that as a page you are
   * missing is what made the Dashboard peek say three where the card listed
   * two. Same arithmetic, same answer, wherever it is asked.
   *
   * The consequence worth stating: a domain whose only non-matching row IS its
   * hero now reports no coverage gap. That is correct. The work there is
   * "deepen this page against that rival", which is what Missing sections says,
   * not "go and write a page you already have".
   */
  hasTopicOwnershipCoverageGap(): boolean {
    return this.hasReviewSupportingPagesGaps();
  }

  hasSupportingPagesSectionGaps(): boolean {
    return hasMissingSectionGaps(this.v2RowsForMarkedCompetitors(this.mergedV2Rows()));
  }

  hasSupportingPagesListInventoryGaps(): boolean {
    return hasListInventoryGaps(this.v2RowsForMarkedCompetitors(this.mergedV2Rows()));
  }

  hasSupportingPagesRankDrivenGaps(): boolean {
    return hasRankDrivenGaps(this.v2RowsForMarkedCompetitors(this.mergedV2Rows()));
  }

  hasSupportingPagesActionableGap(): boolean {
    return (
      this.hasTopicOwnershipCoverageGap() ||
      this.hasSupportingPagesSectionGaps() ||
      this.hasSupportingPagesListInventoryGaps() ||
      this.hasSupportingPagesRankDrivenGaps() ||
      this.topSerpRivalRankDrivenGaps().length > 0
    );
  }

  supportingPagesColumnsReady(): boolean {
    return supportingPagesColumnsReady(this.supportingPagesGateState());
  }

  /**
   * The same card, reduced to the one question a review decision needs.
   *
   * Rules and reasoning live in `utils/review-supporting-pages`. This only
   * hands over the two bits of formatting the deep-dive card already owns, so
   * a path and a traffic figure read identically on both surfaces.
   */
  reviewSupportingPagesColumns(): ReviewSupportingPagesColumn[] {
    return buildReviewSupportingPagesColumns(
      this.getSupportingPagesColumns(),
      {
        path: (url: string) => this.topicOwnershipPagePath(url),
        traffic: (value?: number) => this.topicOwnershipPageTraffic(value)
      },
      this.src.ownSiteUrls,
      this.ownPagePassages()
    );
  }

  /** The new-page ideas the card leaves out because this page already covers them, with the quote. */
  reviewNewPageIdeasAlreadyOnPage(): ReviewNewPageIdeaOnPage[] {
    if (!this.src.embedReviewSurface || !this.supportingPagesColumnsReady()) return [];
    return reviewNewPageIdeasAlreadyOnPage(this.getSupportingPagesColumns(), this.src.ownSiteUrls, this.ownPagePassages());
  }

  hasReviewSupportingPagesGaps(): boolean {
    return hasReviewSupportingPagesGaps(this.reviewSupportingPagesColumns());
  }

  /**
   * "What they cover that you don't", on the review surface.
   *
   * This was already computed and already worded — `evidenceMissingSections`
   * has said it on the Dashboard peek for as long as the peek has existed — and
   * the review surface, which is the screen you reach BY clicking that peek,
   * did not show it. It offered a Compare button instead: a tool where the
   * answer was already sitting in the payload.
   *
   * Sections by name rather than a count, because the count is not the work.
   * "3 sections" is a number to interpret; "PMP® Exam Formula Cheat Sheet" is a
   * thing to write.
   */
  reviewMissingSections(): ReviewMissingSectionsView | null {
    if (!this.src.embedReviewSurface) return null;
    if (this.comparisonHeldBack()) return null;
    if (this.supportingPagesSettling()) return null;
    return reviewMissingSections(
      sectionGapsForV2Row(this.serpRivalEvalRow()),
      this.src.pageCompare?.target?.domain || '',
      // The rival PAGE. `activeSerpRivalCoverage` is the same guarded read the
      // rows come from, so the link cannot point at a different rival than the
      // sections were computed against — the argano/adobe misattribution again.
      this.activeSerpRivalCoverage()?.rivalUrl || null,
      this.ownPagePassages()
    );
  }

  /** The rival's sections the card leaves out because our page already has them, with the quote. */
  reviewSectionsAlreadyOnPage(): ReviewAlreadyOnPageSection[] {
    if (!this.src.embedReviewSurface) return [];
    if (this.comparisonHeldBack()) return [];
    if (this.supportingPagesSettling()) return [];
    return splitSectionGapsByOwnPage(sectionGapsForV2Row(this.serpRivalEvalRow()), this.ownPagePassages()).onPage;
  }

  /**
   * Queries a rival outranks you on, on the review surface.
   *
   * The fourth kind of finding this card can hold, and the one the review was
   * dropping — which is how a card ended up asserting GAP FOUND over "Nothing
   * on this topic that they have and you don't". `hasSupportingPagesActionableGap`
   * counts these, so the badge was right and the body was missing them.
   */
  reviewRankGaps(): ReviewRankGapsView | null {
    if (!this.src.embedReviewSurface) return null;
    if (this.comparisonHeldBack()) return null;
    if (this.supportingPagesSettling()) return null;
    // Name AND page from the same guarded read the rows come from. The domain
    // used to be picked live off the SERP while the rows came from the stored
    // evaluation — the argano/adobe misattribution — and a link built from the
    // live pick could point at a page that produced none of these queries.
    const coverage = this.activeSerpRivalCoverage();
    return reviewRankGaps(
      this.topSerpRivalRankDrivenGaps(),
      this.topEditorialSerpRival()?.domain ?? null,
      coverage?.rivalUrl || null
    );
  }

  /**
   * Findings on this page that still need an answer.
   *
   * Site-wide defects already decided this period drop out — see
   * `utils/site-wide-suppression` for why, and for the deliberate consequence
   * that a later page shows fewer findings than it has. Only the review surface
   * suppresses; URL Detail lists everything, always.
   */
  reviewTechnicalIssues(issues: OnpageIssueView[]): OnpageIssueView[] {
    if (!this.src.embedReviewSurface) return issues;
    const tagged = technicalReviewIssues({ issues, siteWideCodes: this.reviewSiteWideIssueCodes() });
    const decided = decidedSiteWideCodes(this.src.issueRecords.values());
    const open = splitSettledSiteWide(tagged, decided).open;
    const keys = new Set(open.map((i) => i.code));
    return issues.filter((i) => keys.has(i.code));
  }

  /** "6 site-wide issues — already decided this period." Null when none are. */
  reviewSettledSiteWideLabel(issues: OnpageIssueView[]): string | null {
    if (!this.src.embedReviewSurface) return null;
    const tagged = technicalReviewIssues({ issues, siteWideCodes: this.reviewSiteWideIssueCodes() });
    const decided = decidedSiteWideCodes(this.src.issueRecords.values());
    return settledSiteWideLabel(splitSettledSiteWide(tagged, decided).settled.length);
  }

  technicalIssueScopeLabel(issue: OnpageIssueView): string | null {
    if (!this.src.embedReviewSurface) return null;
    const [tagged] = technicalReviewIssues({
      issues: [issue],
      siteWideCodes: this.reviewSiteWideIssueCodes()
    });
    return tagged ? technicalScopeLabel(tagged.scope) : null;
  }

  /**
   * The domain's site-wide codes as they apply to this page on the review surface: without a check
   * the page's stored HTML shows to be its own (`storedHtmlScopesCheckToPage`), such as alt text
   * missing only on the article's screenshots. Null while the rollup has not answered.
   *
   * Everything on the review surface that splits site-wide from page reads this — the tag, the
   * settled filter, the export's "in the site template", and the belt's work item and ticks — so
   * a page-scoped check is the page's work everywhere at once. URL Detail keeps the rollup's set.
   */
  reviewSiteWideIssueCodes(): Set<string> | null {
    const codes = this.src.siteWideIssueCodes;
    if (!codes || !this.src.embedReviewSurface) return codes;
    const own = [...codes].filter((code) => storedHtmlScopesCheckToPage(code, this.src.analysisData));
    if (!own.length) return codes;
    return new Set([...codes].filter((code) => !own.includes(code)));
  }

  /** The rules live in `utils/supporting-pages-render-gate` — see the note there. */
  supportingPagesGateState(): SupportingPagesGateState {
    return {
      topicOwnershipStatus: this.src.topicOwnershipStatus as SupportingPagesLoadStatus,
      topicOwnershipV2Status: this.src.topicOwnershipV2Status as SupportingPagesLoadStatus,
      markedSerpEvalPending: this.src.markedSerpEvalPending,
      ownSiteUrlsPending: this.src.ownSiteUrls === null,
      columnCount: this.getSupportingPagesColumns().length,
      settledOnly: this.src.embedReviewSurface
    };
  }

  supportingPagesSettling(): boolean {
    return supportingPagesSettling(this.supportingPagesGateState());
  }

  getSupportingPagesColumns(): SupportingPagesUnifiedColumn[] {
    const extractDomain = (pageUrl: string) => this.extractDomainFromUrl(pageUrl);
    const markedDomains = this.src.markedCompetitorDomains.map((d) => this.normalizeSerpDomain(d));
    const markedV2Rows = this.allMarkedV2Rows();
    const preferredByDomain = this.markedCompetitorUrlsOnSerp();
    let columns: SupportingPagesUnifiedColumn[] = [];

    const snapshotDomains = this.src.topicOwnershipDomains.filter(
      (block) =>
        block.isYourSite ||
        markedDomains.includes(this.normalizeSerpDomain(block.domain))
    );

    if (this.src.topicOwnershipStatus === 'ready' && snapshotDomains.length) {
      columns = buildSupportingPagesUnifiedColumns({
        domains: snapshotDomains,
        focalUrl: this.src.url || '',
        v2Rows: markedV2Rows,
        preferredCompetitorUrlByDomain: preferredByDomain,
        extractDomain,
        filterRows: (rows) =>
          this.getTopicOwnershipRowsForDisplay({ domain: '', isYourSite: false, rows })
      });
    } else if (this.src.topicOwnershipStatus === 'ready' && markedV2Rows.length) {
      columns = buildSupportingPagesV2OnlyColumns({
        focalUrl: this.src.url || '',
        yourSiteDomain: extractDomain(this.src.url || '') || 'Your site',
        v2Rows: markedV2Rows,
        extractDomain
      });
    } else if (this.src.topicOwnershipStatus === 'ready') {
      const yourSiteDomain = extractDomain(this.src.url || '');
      if (yourSiteDomain) {
        columns = [
          {
            domain: yourSiteDomain,
            isYourSite: true,
            heroUrl: '',
            heroRow: null,
            supportingRows: [],
            v2Row: null
          }
        ];
      }
    }

    return attachV2RowsToSupportingPageColumns(
      augmentSupportingPagesColumns({
        columns,
        v2Rows: markedV2Rows,
        serpSeeds: this.supportingPagesSerpSeeds(),
        extractDomain,
        markedDomains
      }),
      markedV2Rows,
      extractDomain
    );
  }

  allMarkedV2Rows(): SupportingPagesV2RowResult[] {
    const fromMerged = this.v2RowsForMarkedCompetitors(this.mergedV2Rows());
    const keys = new Set(fromMerged.map((r) => normalizeUrlForCompare(r.competitorUrl)));
    const extra = this.src.markedSerpCompetitorV2Rows.filter(
      (r) => !keys.has(normalizeUrlForCompare(r.competitorUrl))
    );
    return [...fromMerged, ...extra];
  }

  v2RowsForMarkedCompetitors(
    rows: SupportingPagesV2RowResult[]
  ): SupportingPagesV2RowResult[] {
    return rows.filter((row) => {
      const domain = this.normalizeSerpDomain(this.extractDomainFromUrl(row.competitorUrl));
      return (
        !!domain &&
        this.src.markedCompetitorDomains.some((m) => this.normalizeSerpDomain(m) === domain)
      );
    });
  }

  /** Marked competitor URL on this page's SERP, keyed by normalized domain. */
  markedCompetitorUrlsOnSerp(): Record<string, string> {
    const out: Record<string, string> = {};
    const serpResults = this.src.pageSerp?.results || [];
    for (const marked of this.src.markedCompetitorDomains) {
      const key = this.normalizeSerpDomain(marked);
      const hit = serpResults.find((row) => this.normalizeSerpDomain(row.domain) === key);
      if (hit?.url?.trim()) out[key] = hit.url.trim();
    }
    return out;
  }

  /** Marked competitors on this page's SERP — inject when topic snapshot has no rows. */
  supportingPagesSerpSeeds(): SerpCompetitorColumnSeed[] {
    const seeds: SerpCompetitorColumnSeed[] = [];
    const seen = new Set<string>();

    for (const marked of this.src.markedCompetitorDomains) {
      const key = this.normalizeSerpDomain(marked);
      if (!key || seen.has(key)) continue;
      const hit = (this.src.pageSerp?.results || []).find(
        (row) => this.normalizeSerpDomain(row.domain) === key
      );
      if (!hit?.url?.trim()) continue;
      seen.add(key);
      seeds.push({ domain: key, url: hit.url.trim(), source: 'marked' });
    }

    return seeds;
  }

  normalizeSerpDomain(domain: string): string {
    return (domain || '').toLowerCase().trim().replace(/^www\./, '');
  }

  focalQueryForRankDrivenGaps(): string | undefined {
    const serpQuery = this.pageSerpTargetQueryLabel();
    if (serpQuery && serpQuery !== '—') return serpQuery;
    const claim = this.pageDemandClaimLabel();
    if (claim && claim !== '—') return claim;
    return undefined;
  }

  /**
   * Queries the TOP SERP RIVAL outranks you on.
   *
   * `activeSerpRivalCoverage()`, not the raw stored coverage. The stored row is
   * whichever rival the evaluation last ran against; the guard checks it is
   * still the rival the current SERP names, and everything else on this card
   * already goes through it.
   *
   * Without the guard the rows and the sentence above them came from different
   * places: the rows from the stored evaluation, the domain in "X outranks you
   * on 3 queries" from a live pick off the SERP. Two loads of the same page
   * showed the same three queries attributed first to argano.com and then to
   * business.adobe.com. A wrong number is bad; naming the wrong competitor
   * sends someone to read the wrong company's page.
   *
   * When the stored evaluation is stale the rows now disappear instead of
   * appearing under a name that did not produce them. Showing nothing is the
   * right failure here — the card is evidence, and evidence attributed to the
   * wrong source is worse than absent.
   */
  topSerpRivalRankDrivenGaps(): RankDrivenSectionGap[] {
    // One gate for every reader of these rows — the belt's card, the Dashboard
    // peek's count and title, the "does this page have an actionable gap"
    // predicate and the work-item composer. Switching the card off while the
    // peek still counted the rows would put a badge over a card that never
    // renders, which is the shape of half the bugs found in this queue.
    if (!RANK_DRIVEN_GAPS_ENABLED) return [];
    const row = this.activeSerpRivalCoverage()?.evalRow;
    return rankDrivenGapsForV2Row(row, {
      focalQuery: this.focalQueryForRankDrivenGaps(),
      focalAnalysis: this.src.analysisData,
      rivalAnalysis: undefined
    });
  }

  /**
   * The badges on the review surface count; everywhere else they judge.
   *
   * On review these cards only render when there IS something, so a verdict
   * badge reads GAP FOUND / ISSUES FOUND / WARNINGS FOUND on every card, on
   * every page, forever — the most valuable corner of each card spent saying
   * nothing. A count lets an operator size up a page without reading a body,
   * which is the whole job of the surface.
   *
   * It also matches the Dashboard peek, which has always said "1 SECTION" and
   * "2 PAGE-SPECIFIC" rather than a verdict — so the review was the odd one out.
   *
   * Inventory keeps the verdicts. Its cards render whether or not there is a
   * gap, so "NO GAP" is real information there and a count of zero is not.
   */
  reviewCountBadge(n: number, one: string, many: string): string {
    return n === 1 ? `1 ${one}` : `${n} ${many}`;
  }

  technicalIssuesActionChipLabel(): string {
    if (this.src.embedReviewSurface) {
      return this.reviewCountBadge(
        this.reviewTechnicalIssues(this.technicalCriticalIssueViews()).length,
        'ISSUE',
        'ISSUES'
      );
    }
    return this.hasTechnicalCriticalIssues() ? 'ISSUES FOUND' : 'NO ISSUES';
  }

  technicalWarningsActionChipLabel(): string {
    if (this.src.embedReviewSurface) {
      return this.reviewCountBadge(
        this.reviewTechnicalIssues(this.technicalWarningIssueViews()).length,
        'WARNING',
        'WARNINGS'
      );
    }
    return this.hasTechnicalWarnings() ? 'WARNINGS FOUND' : 'NO WARNINGS';
  }

  pageIntentMatchActionChipLabel(): string {
    const d = this.src.pageIntentDiagnosis;
    if (!d || d.serpFamily === 'unknown') return '—';
    if (d.serpFamily === 'mixed') return 'MIXED SERP';
    return d.intentAlignedWithSerp ? 'MATCH' : 'NO MATCH';
  }

  pageIntentMatchActionChipTone(): DiagnosisTone {
    const d = this.src.pageIntentDiagnosis;
    if (!d || d.serpFamily === 'unknown') return 'muted';
    // A mixed SERP your page's own type already ranks on is not a decision —
    // see `mixedSerpAlignment`. Green, so it leaves the review queue.
    if (this.pageTypeMixedAlignment()) return 'ok';
    if (d.serpFamily === 'mixed') return 'neutral';
    return d.intentAlignedWithSerp
      ? 'ok'
      : 'warn';
  }

  serpPhraseMatchActionChipTone(): DiagnosisTone {
    const d = this.src.pageIntentDiagnosis;
    if (!d) return 'muted';
    const missing = d.missingTerms?.length ?? 0;
    const structureMismatch =
      Boolean(d.answerStructureInAppRoute) ||
      (Boolean(d.answerStructureFamily) &&
        d.answerStructureFamily !== 'unknown' &&
        d.answerStructureFamily !== 'fragmented' &&
        d.answerStructureAligned === false);
    if (missing === 0 && !structureMismatch) {
      return this.showFragmentedSerpVocabularyNote()
        ? 'neutral'
        : 'ok';
    }
    return 'warn';
  }

  /**
   * Your page's type is one of the shapes this mixed SERP ranks, or null.
   *
   * Memoised per diagnosis rather than recomputed: the tone, the brief line and
   * the card gate all ask, and three answers to one question is the failure
   * this codebase keeps finding.
   */
  pageTypeMixedAlignment(): MixedSerpAlignment | null {
    const d = this.src.pageIntentDiagnosis;
    if (!d) return null;
    if (this.pageTypeMixedAlignmentFor !== d) {
      this.pageTypeMixedAlignmentFor = d;
      this.pageTypeMixedAlignmentCache = mixedSerpAlignment(
        d.serpFamily,
        d.pageTypeFamily || d.bestSegmentFamily,
        d.serpRowIntents,
        d.serpMixedIntentSamples
      );
    }
    return this.pageTypeMixedAlignmentCache;
  }

  pageTypeMixedAlignmentFor: PageIntentDiagnosis | null = null;

  pageTypeMixedAlignmentCache: MixedSerpAlignment | null = null;

  /**
   * Two SERP results that ARE the page type this card says Google expects.
   *
   * The verdict ("SERP expects definition") was unfalsifiable on the belt: the
   * only way to check it was to open Google, which is the click the belt
   * exists to remove. The titles also answer the question the verdict raises —
   * what would a definition page look like for this query.
   *
   * Review surface only. Inventory and URL detail already show the whole SERP
   * table with an intent column a row away, so there the examples would be a
   * second copy of what is on screen; on the belt that table is not there.
   *
   * The families live on the diagnosis and the titles on the SERP snapshot, so
   * they are joined by position here. Memoised per (diagnosis, snapshot) pair:
   * the template calls this on every change-detection pass.
   */
  pageTypeExamples(): PageTypeExample[] {
    if (!this.src.embedReviewSurface) return [];
    const d = this.src.pageIntentDiagnosis;
    const serp = this.src.pageSerp ?? null;
    if (!d) return [];
    if (this.pageTypeExamplesForDiagnosis !== d || this.pageTypeExamplesForSerp !== serp) {
      this.pageTypeExamplesForDiagnosis = d;
      this.pageTypeExamplesForSerp = serp;
      const titleByPosition = new Map<number, { domain: string; title: string }>();
      for (const row of serp?.results || []) {
        titleByPosition.set(row.position, { domain: row.domain || '', title: row.title || '' });
      }
      const verdict = this.reviewPageTypeAsksForChange() ? this.pageTypeWholePageVerdict() : null;
      const route = verdict ? this.pageTypeVerdictRoute() : null;
      const routeSources = (d.answerStructureInAppRoute?.sources || []).filter((s) => s.quote);
      if (route && routeSources.length) {
        // The verdict names the app's own route: the examples are the results that give it, in
        // their own words from the sentence the route starts in, as the steps card quoted them (§0cb).
        this.pageTypeExamplesCache = routeSources.slice(0, 2).map((s) => ({
          position: s.position,
          domain: s.domain.replace(/^www\./, ''),
          title: this.reviewTrimPlain(s.snippet || s.quote)
        }));
      } else if (verdict?.examplePositions.length) {
        // The results the verdict counted for the page's own job, not a family match: a result
        // for the opposite job, or a forum question, is not the shape to write.
        this.pageTypeExamplesCache = verdict.examplePositions
          .map((position) => ({ position, ...(titleByPosition.get(position) || { domain: '', title: '' }) }))
          .filter((e) => e.domain.trim() && e.title.trim())
          .slice(0, 2)
          .map((e) => ({ position: e.position, domain: e.domain.trim().replace(/^www\./, ''), title: e.title.trim() }));
      } else if (!serp) {
        this.pageTypeExamplesCache = [];
      } else this.pageTypeExamplesCache = serpPageTypeExamples(
        d.serpFamily,
        (d.serpRowIntents || []).map((row) => ({
          position: row.position,
          family: row.family,
          domain: titleByPosition.get(row.position)?.domain,
          title: titleByPosition.get(row.position)?.title
        })),
        serp.yourUrl || this.src.url || ''
      );
    }
    return this.pageTypeExamplesCache;
  }

  pageTypeExamplesForDiagnosis: PageIntentDiagnosis | null = null;

  pageTypeExamplesForSerp: PageSerpSnapshot | null = null;

  pageTypeExamplesCache: PageTypeExample[] = [];

  /** "Ranking as definition" — names what the two examples below are examples OF. */
  pageTypeExamplesLabel(): string {
    if (this.reviewPageTypeAsksForChange() && this.pageTypeWholePageVerdict() && this.pageTypeVerdictRoute()) {
      return 'The app’s own steps in the results';
    }
    const label = this.pageIntentFamilyLabel(this.src.pageIntentDiagnosis?.serpFamily);
    return label ? `Ranking as ${label.toLowerCase()}` : 'Ranking now';
  }

  /**
   * Review surface, and the page-type card is asking the page to change (its tone is not green).
   * Inventory and URL detail keep the diagnosis as it is: the results table beside it is what an
   * operator digs with there.
   */
  reviewPageTypeAsksForChange(): boolean {
    if (!this.src.embedReviewSurface || !this.src.pageIntentDiagnosis) return false;
    return this.pageIntentMatchActionChipTone() !== 'ok';
  }

  /**
   * What the review surface's page-type card tells the writer to do, or null when it has nothing
   * a writer can act on, in which case the card is not shown (owner decision, quality sprint 4
   * Q2).
   *
   * - The rivals agree on a type the page is not, and the pair has a whole-page change
   *   (`pageTypeWholePageVerdict`): that verdict, whatever the page's rank (§0an, quality
   *   sprint 5 A). On a top-ranked page it is framed as keeping the position.
   * - Otherwise, the page ranks in the top `PAGE_TYPE_ADVICE_MAX_OWN_RANK` of the results it was
   *   compared on: null. Google already rewards its type for that search.
   * - The diagnosis names a change: moving the passage that already answers up to the opening, or
   *   adding the type's cues to a page that reads as no type. That line, as it is.
   * - A bare verdict ("SERP expects how-to; your page reads tool") or "pick one page type" on a
   *   mixed result set names no change: shown only with a first change we can name
   *   (`pageTypeFirstChange`), otherwise null.
   */
  reviewPageTypeAdvice(): string | null {
    const d = this.src.pageIntentDiagnosis;
    if (!d) return null;
    const verdict = this.pageTypeWholePageVerdict();
    if (verdict) return verdict.advice;
    if (pageTypeAdviceWithheldByRank(this.src.pageSerp?.yourPosition)) return null;

    const placement = d.bullets.find((b) => b.kind === 'placement');
    if (placement?.pageTypeChange) {
      return placement.text.length > 220 ? `${placement.text.slice(0, 217)}…` : placement.text;
    }
    if (d.serpFamily === 'mixed' || d.serpFamily === 'unknown' || d.intentAlignedWithSerp) return null;

    const yoursFamily = d.pageTypeFamily || d.bestSegmentFamily;
    if (!placement && (!yoursFamily || yoursFamily === 'unknown')) {
      return this.pageTypeMissingCuesLine(d.serpFamily);
    }
    const change = pageTypeFirstChange(d.serpFamily, yoursFamily);
    if (!change) return null;
    return `SERP expects ${this.pageIntentFamilyLabel(d.serpFamily)}; your page reads ${this.pageIntentFamilyLabel(
      yoursFamily
    )}. ${change}`;
  }

  /**
   * The verdict on the whole page's type, with the rival count behind it, or null. Memoised per
   * (diagnosis, snapshot): the card gate, its advice and its brief lines all ask.
   */
  pageTypeWholePageVerdict(): PageTypeWholePageVerdict | null {
    const d = this.src.pageIntentDiagnosis;
    if (!d) return null;
    const serp = this.src.pageSerp ?? null;
    if (this.pageTypeVerdictForDiagnosis !== d || this.pageTypeVerdictForSerp !== serp) {
      this.pageTypeVerdictForDiagnosis = d;
      this.pageTypeVerdictForSerp = serp;
      this.pageTypeVerdictCache =
        d.serpFamily === 'mixed' || d.serpFamily === 'unknown' || d.intentAlignedWithSerp
          ? null
          : pageTypeWholePageVerdict({
              serpFamily: d.serpFamily,
              yourFamily: d.pageTypeFamily || d.bestSegmentFamily,
              vote: d.serpRivalVote,
              // Another page of the site ranking is not this page's position to keep.
              ownPosition: serp?.matchType === 'exact_url' ? serp.yourPosition : null,
              gated: d.pageOfferGated,
              route: this.pageTypeVerdictRoute()
            });
    }
    return this.pageTypeVerdictCache;
  }

  /** The app's own route the results agree on, which the whole-page verdict names (quality sprint 6 G). */
  pageTypeVerdictRoute(): PageTypeVerdictRoute | null {
    const route = this.src.pageIntentDiagnosis?.answerStructureInAppRoute;
    const steps = route?.steps ?? (route?.command ? [route.command] : []);
    return route && steps.length ? { entry: route.entry, steps } : null;
  }

  /**
   * The review surface's page-type verdict already carries the steps, so the answer-structure card
   * would say them twice (quality sprint 6 G: sprint 5's grader, on three pages: "the verdict and
   * the steps item repeat each other"). True when the verdict is shown and the answer-structure
   * card's instruction is steps: the app's own route (named in the verdict's change) or numbered
   * how-to steps (the verdict's "steps first").
   */
  pageTypeVerdictFoldsSteps(): boolean {
    if (!this.src.embedReviewSurface) return false;
    const d = this.src.pageIntentDiagnosis;
    if (!d) return false;
    if (!d.answerStructureInAppRoute && d.answerStructureFamily !== 'numbered_howto') return false;
    return this.reviewPageTypeAsksForChange() && !!this.pageTypeWholePageVerdict() && this.showEmbedSerpIntentCard();
  }

  pageTypeVerdictForDiagnosis: PageIntentDiagnosis | null = null;

  pageTypeVerdictForSerp: PageSerpSnapshot | null = null;

  pageTypeVerdictCache: PageTypeWholePageVerdict | null = null;

  /** Page-type card brief (serpFamily vs yours) — no answer-structure lines. */
  pageTypeBriefLines(): string[] {
    const d = this.src.pageIntentDiagnosis;
    if (!d) return [];
    // The whole-page verdict on the review surface: the change, then the count it rests on.
    const verdict = this.reviewPageTypeAsksForChange() ? this.pageTypeWholePageVerdict() : null;
    if (verdict) return [verdict.advice, verdict.evidence];
    const lines: string[] = [];
    const serpLabel = this.pageIntentFamilyLabel(d.serpFamily);
    const yoursFamily = d.pageTypeFamily || d.bestSegmentFamily;
    const yoursLabel = this.pageIntentFamilyLabel(yoursFamily);

    // Prefer the placement/why bullet (what's missing) over a vague “SERP mostly X”.
    const placement = d.bullets.find((b) => b.kind === 'placement');
    const reviewAdvice = this.reviewPageTypeAsksForChange() ? this.reviewPageTypeAdvice() : null;
    if (reviewAdvice) {
      lines.push(reviewAdvice);
    } else if (placement) {
      const text = placement.text.length > 220 ? `${placement.text.slice(0, 217)}…` : placement.text;
      lines.push(text);
    } else if (d.serpFamily === 'mixed') {
      const aligned = this.pageTypeMixedAlignment();
      if (aligned) {
        // Nothing to pick. Naming the rival that shares the type is what makes
        // this a finding rather than a reassurance.
        const who = aligned.domain
          ? `${aligned.domain}${aligned.position ? ` (#${aligned.position})` : ''}`
          : aligned.position
            ? `#${aligned.position}`
            : 'a top result';
        lines.push(
          `Top results disagree on page type, but ${this.pageIntentFamilyLabel(
            aligned.family
          )} is one of the shapes ranking — ${who} is the same. Nothing to change here.`
        );
      } else if (!(d.serpMixedIntentSamples?.length >= 2)) {
        lines.push('Top results disagree on page type — pick one angle.');
      } else {
        lines.push('Pick one page type to align with.');
      }
    } else if (d.intentAlignedWithSerp) {
      const where = d.pageTypeLabel ? ` (${d.pageTypeLabel})` : '';
      lines.push(`SERP and your page both read as ${serpLabel}${where}.`);
    } else if (yoursFamily === 'unknown' && d.serpFamily !== 'unknown') {
      lines.push(this.pageTypeMissingCuesLine(d.serpFamily));
    } else if (yoursFamily && yoursFamily !== 'unknown' && d.serpFamily !== 'unknown') {
      lines.push(`SERP expects ${serpLabel}; your page reads ${yoursLabel}.`);
    } else if (d.serpFamily !== 'unknown') {
      lines.push(`SERP mostly ${serpLabel}.`);
    }

    if (lines.length < 2 && !placement) {
      const extra = d.bullets.find((b) => b.kind === 'claim_mismatch');
      if (extra) {
        const text = extra.text.length > 140 ? `${extra.text.slice(0, 137)}…` : extra.text;
        lines.push(text);
      }
    }
    return lines.slice(0, 2);
  }

  static readonly PAGE_TYPE_SIGNAL_CUES: Record<string, string> = {
    definition: '“is a/an”, “refers to”, “defined as”, or “means”',
    how_to: '“how to”, “step-by-step”, “steps to”, or “guide to”',
    listicle: '“best”, “top N”, “alternatives”, or “competitors”',
    versus: '“vs”, “versus”, “difference between”, or “compared to”',
    template: '“template”, “download”, “PDF”, or “worksheet”',
    pricing: '“pricing”, “plans”, “per month”, or price language'
  };

  pageTypeMissingCuesLine(serpFamily: string): string {
    const label = this.pageIntentFamilyLabel(serpFamily);
    const cues = ReviewPageModel.PAGE_TYPE_SIGNAL_CUES[serpFamily];
    if (!cues) {
      return `No section reads as ${label} — strengthen that page type in the intro or primary heading.`;
    }
    return `No section reads as ${label} — crawled copy lacks ${label} cues (${cues}). Add clear ${label} framing in the intro or primary heading.`;
  }

  /**
   * The top results agree on no answer shape, so there is nothing to compare to.
   *
   * `fragmented` is the analyser's own word for it and `unknown` is its absence.
   * Either way the card has no rival to hold this page against: on
   * /templates/project-post-mortem-template every winner excerpt carries zero
   * structure-evidence spans, which is why "How they answer" rendered nothing
   * while "How you answer" quoted the page back at the operator.
   */
  serpAnswerStructureIsFragmented(): boolean {
    const family = this.src.pageIntentDiagnosis?.answerStructureFamily;
    return !family || family === 'fragmented' || family === 'unknown';
  }

  answerStructureBriefLines(): string[] {
    const d = this.src.pageIntentDiagnosis;
    if (!d) return [];
    const lines: string[] = [];

    const structureBullet = d.bullets.find((b) => b.kind === 'answer_structure');
    if (structureBullet) {
      const text =
        structureBullet.text.length > 180
          ? `${structureBullet.text.slice(0, 177)}…`
          : structureBullet.text;
      lines.push(text);
    } else if (d.answerStructureCue && !this.serpAnswerStructureIsFragmented()) {
      // The cue is the model's read of a consensus. When the consensus did not
      // hold — `fragmented`, ratio 0 — the cue survives in the payload and used
      // to be printed as the finding: "SERP answers via item lists" over a
      // diagnosis that says the top results share no shape at all. The Dashboard
      // read the honest line and called the row Clear; the belt read this one
      // and asked for a decision. Same page, opposite verdicts.
      const cue =
        d.answerStructureCue.length > 180
          ? `${d.answerStructureCue.slice(0, 177)}…`
          : d.answerStructureCue;
      lines.push(cue);
    }

    if (lines.length < 2) {
      const extra = d.bullets.find(
        (b) => b.kind === 'lexical_gap' || b.kind === 'vocabulary_fragmented'
      );
      if (extra) {
        const text = extra.text.length > 140 ? `${extra.text.slice(0, 137)}…` : extra.text;
        lines.push(text);
      }
    }
    return lines.slice(0, 2);
  }

  showSerpPhraseMatchCard(): boolean {
    if (!this.src.pageIntentDiagnosis) return false;
    return (
      Boolean(this.src.pageIntentDiagnosis.answerStructureFamily) ||
      this.hasPageIntentCompare() ||
      this.hasLexicalVocabChips() ||
      this.showFragmentedSerpVocabularyNote()
    );
  }

  contentFreshnessVerdict(): string | null {
    return comparePageFreshnessToSerp(
      this.pageContentLastModifiedDate(),
      this.serpWinnerPublishedDates()
    ).verdict;
  }

  pageSerpRowIntentFamily(row: PageSerpResultRow): string {
    const hit = this.src.pageIntentDiagnosis?.serpRowIntents?.find((i) => i.position === row.position);
    return hit?.family || 'unknown';
  }

  pageSerpRowIntentLabel(row: PageSerpResultRow): string {
    return this.pageIntentFamilyLabel(this.pageSerpRowIntentFamily(row));
  }

  hasPageIntentDiagnosis(): boolean {
    return (this.src.pageIntentDiagnosis?.bullets?.length || 0) > 0;
  }

  hasPageIntentCompare(): boolean {
    const d = this.src.pageIntentDiagnosis;
    if (!d) return false;
    return (
      Boolean(d.answerStructurePageText?.trim()) ||
      Boolean(d.yourBestSection?.text) ||
      (d.serpWinnerExcerpts?.length || 0) > 0
    );
  }

  /** Full page passage(s) for answer-structure match / closest (Our page column). */
  searchIntentStructurePagePassages(): Array<{
    label: string;
    text: string;
    matchKind: 'match' | 'closest';
    family?: string;
    evidenceSpans: string[];
    questionSpans: string[];
    headingIsQuestion: boolean;
    fitPct: number | null;
  }> {
    const d = this.src.pageIntentDiagnosis;
    if (!d) return [];
    const fromApi = (d.answerStructurePagePassages || [])
      .filter((p) => (p.text || '').trim() && (p.label || '').trim())
      .map((p) => ({
        label: p.label.trim(),
        text: p.text.trim(),
        matchKind: (p.matchKind === 'closest' ? 'closest' : 'match') as 'match' | 'closest',
        family: p.family,
        evidenceSpans: (p.evidenceSpans || d.answerStructurePageEvidenceSpans || []).filter(Boolean),
        questionSpans: (p.questionSpans || []).filter(Boolean),
        headingIsQuestion: Boolean(p.headingIsQuestion),
        fitPct: Number.isFinite(p.fitRatio) ? Math.round(Number(p.fitRatio) * 100) : null
      }));
    if (fromApi.length) {
      return dedupeOverlappingStructurePassages(fromApi, 3);
    }

    const text = (d.answerStructurePageText || '').trim();
    const label = (d.answerStructurePageLabel || '').trim();
    if (!text || !label) return [];
    return [
      {
        label,
        text,
        matchKind: d.answerStructureAligned ? 'match' : 'closest',
        family: d.answerStructurePageFamily || undefined,
        evidenceSpans: (d.answerStructurePageEvidenceSpans || []).filter(Boolean),
        questionSpans: [],
        headingIsQuestion: false,
        fitPct: null
      }
    ];
  }

  hasLexicalVocabChips(): boolean {
    return this.serpVocabChipRows().length > 0;
  }

  /**
   * Structurally aligned AND no vocabulary to place or add.
   *
   * "Aligned" is read from the diagnosis rather than from the badge, because
   * the badge already collapses three states into one label and this needs the
   * structural half alone. Only the two groups the review card actually renders
   * count as something to send: `onPage` is a placement call and `missing` is a
   * gap. A term already in the right place asks for nothing.
   */
  reviewAnswerStructureHasNothingToSend(): boolean {
    const d = this.src.pageIntentDiagnosis;
    if (!d) return false;
    const family = d.answerStructureFamily;
    const decisiveSerp = Boolean(family) && family !== 'unknown' && family !== 'fragmented';
    if (!decisiveSerp || d.answerStructureAligned !== true) return false;
    const vocab = reviewAnswerVocabGroups(this.serpVocabChipRows());
    // Null means the composer found nothing worth grouping — nothing to send.
    if (!vocab) return true;
    return vocab.onPage.length === 0 && vocab.missing.length === 0;
  }

  /** Green = in chosen answer-structure passage; yellow = elsewhere on page; red = absent. */
  serpVocabChipRows(): Array<{ term: string; tone: 'in_passage' | 'on_page' | 'absent' }> {
    const d = this.src.pageIntentDiagnosis;
    if (!d) return [];

    // The server's answer when it sent one. It reads every crawled section;
    // this component only ever had three of them, so a word written in the
    // page's fifth section came back red here — the "boundaries" bug, where a
    // card said "never used" about a page's own opening sentence.
    const placed = d.serpTermPlacement;
    if (placed?.length) {
      return placed.slice(0, 16).map((row) => ({
        term: row.term,
        tone:
          row.where === 'in_answer'
            ? ('in_passage' as const)
            : row.where === 'on_page'
              ? ('on_page' as const)
              : ('absent' as const)
      }));
    }

    // Payloads written before placement existed. The fallback keeps the two
    // tones it can prove — a term in the passage, and a term in a section this
    // component holds — and calls everything else `on_page` rather than
    // `absent`: unmeasured must never render as a claim that the page never
    // uses the word.
    const terms = this.serpVocabChipTerms();
    if (!terms.length) return [];
    const passage = (d.answerStructurePageText || '').trim();
    const pageTexts = [
      passage,
      d.yourBestSection?.text || '',
      d.yourIntroSection?.text || ''
    ].filter((t) => t.trim().length > 0);

    return terms.map((term) => {
      if (passage && this.serpTextHasVocabTerm(passage, term)) {
        return { term, tone: 'in_passage' as const };
      }
      return { term, tone: 'on_page' as const };
    });
  }

  serpVocabChipTerms(): string[] {
    const d = this.src.pageIntentDiagnosis;
    if (!d) return [];
    const fromContract = (d.serpLexicalTerms || []).filter(Boolean);
    if (fromContract.length) return fromContract.slice(0, 16);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const term of [...(d.presentLexicalTerms || []), ...(d.missingTerms || [])]) {
      const key = foldLexicalToken(term);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(term);
      if (out.length >= 16) break;
    }
    return out;
  }

  serpTextHasVocabTerm(text: string, term: string): boolean {
    return buildSerpFoldedTermSegments(text, term, foldLexicalToken).some((s) => s.emphasized);
  }

  searchIntentWinnerExcerptsAll(): NonNullable<PageIntentDiagnosis['serpWinnerExcerpts']> {
    return this.src.pageIntentDiagnosis?.serpWinnerExcerpts || [];
  }

  searchIntentExcerptSegments(excerpt: {
    excerpt: string;
    highlightTerms?: string[];
    structureEvidenceSpans?: string[];
  }): SerpSnippetSegment[] {
    // Top winner excerpts: structure evidence only (vocab/DFS stays on chip hover below).
    const structure = (excerpt.structureEvidenceSpans || []).filter(Boolean);
    return buildSerpSnippetSegments(excerpt.excerpt || '', structure.length ? structure : []);
  }

  /** Top snippets lacked a shared repeated-vocabulary contract (chips intentionally omitted). */
  showFragmentedSerpVocabularyNote(): boolean {
    const d = this.src.pageIntentDiagnosis;
    if (!d) return false;
    const shared =
      d.serpVocabularyShared ?? (d.serpLexicalTerms?.length ?? 0) > 0;
    if (shared) return false;
    return this.hasPageIntentCompare() || this.hasPageIntentDiagnosis();
  }

  static readonly INTENT_FAMILY_LABELS: Record<string, string> = {
    definition: 'definition',
    how_to: 'how-to',
    listicle: 'list / roundup',
    versus: 'versus',
    template: 'template',
    pricing: 'pricing',
    mixed: 'mixed',
    unknown: 'unknown'
  };

  pageIntentFamilyLabel(family: string | null | undefined): string {
    if (!family || family === 'unknown') return 'unknown';
    return ReviewPageModel.INTENT_FAMILY_LABELS[family] || family;
  }

  pageIntentYourPageFamilyLabel(): string {
    const d = this.src.pageIntentDiagnosis;
    return this.pageIntentFamilyLabel(d?.pageTypeFamily || d?.bestSegmentFamily);
  }

  showPageIntentAlignmentRow(): boolean {
    return Boolean(this.src.pageIntentDiagnosis);
  }

  hasPageSignalKind(kind: InventoryPageSignalKind): boolean {
    return this.src.pageSignals.some((s) => s.kind === kind);
  }

  /** Incoming links not already sent to Work items (Issues triage on the beneficiary URL). */
  pageSignalInternalLinkDetailsPendingTriage(): ActionPlanInternalLinkRow[] {
    return filterInternalLinksPendingIssuesTriage(
      this.src.internalLinkSignalRows,
      this.src.pageUrlId.trim(),
      internalLinkSuggestionKeysInWorkItems(this.src.issueRecords)
    );
  }

  pageSignalUnclusteredDetail(): ActionPlanUnclusteredPageRow | null {
    const urlId = this.src.pageUrlId.trim();
    if (!urlId) return null;
    return this.src.unclusteredSignalRows.find((row) => row.status === 'open' && row.urlId === urlId) || null;
  }

  unclusteredRecommendationLabel(rec: ActionPlanUnclusteredPageRow['recommendation']): string {
    return unclusteredRecommendationLabel(rec);
  }

  /**
   * The advice itself: the cluster to assign when the siblings name one, the page to merge into when
   * another page covers this one, else the bare recommendation.
   */
  unclusteredActionLabel(row: ActionPlanUnclusteredPageRow): string {
    if (row.recommendation === 'assign_cluster' && row.clusterFit) return unclusteredClusterAction(row.clusterFit);
    return unclusteredRecommendationLabel(row.recommendation, row.coveredBy);
  }

  /** Which pages the cluster advice rests on, in one sentence (null when there is no cluster advice). */
  unclusteredClusterFitSentence(row: ActionPlanUnclusteredPageRow): string | null {
    if (row.recommendation !== 'assign_cluster' || !row.clusterFit) return null;
    return unclusteredClusterFitSentence(row.clusterFit);
  }

  /**
   * The record the unclustered advice rests on (null on a row without one): the traffic record, and on
   * a kept page first why its job is not search.
   */
  unclusteredEvidenceSentence(row: ActionPlanUnclusteredPageRow): string | null {
    return row.trafficEvidence
      ? unclusteredEvidenceSentenceFor(row.recommendation, row.trafficEvidence, row.pageRole, row.coveredBy)
      : null;
  }

  /**
   * The page's technical findings. On the review surface, without the checks its own stored crawl
   * contradicts (`review-technical-checks`): everything the card renders, ticks, counts and sends
   * reads these two, and so does the export, so one filter here is one rule for all of them. URL
   * Detail lists every check.
   */
  technicalCriticalIssueViews(): OnpageIssueView[] {
    return this.reviewShownTechnicalChecks(
      buildOnpageTechnicalIssueViews(this.src.analysisData, this.src.sharedIssueCauses).critical
    );
  }

  technicalWarningIssueViews(): OnpageIssueView[] {
    return this.reviewShownTechnicalChecks(
      buildOnpageTechnicalIssueViews(this.src.analysisData, this.src.sharedIssueCauses).warnings
    );
  }

  private reviewShownTechnicalChecks(views: OnpageIssueView[]): OnpageIssueView[] {
    if (!this.src.embedReviewSurface) return views;
    return reviewTechnicalChecks(views, this.src.analysisData).shown;
  }

  hasTechnicalCriticalIssues(): boolean {
    return this.technicalCriticalIssueViews().length > 0;
  }

  hasTechnicalWarnings(): boolean {
    return this.technicalWarningIssueViews().length > 0;
  }

  /** Top editorial SERP rival for the current snapshot (lowest position that beats you). */
  topEditorialSerpRival(): { url: string; domain: string } | null {
    return findTopSerpRival(this.src.pageSerp, this.extractDomainFromUrl(this.src.url || ''));
  }

  /** Precomputed rival coverage when it matches the current SERP query + top rival. */
  activeSerpRivalCoverage(): SerpRivalCoverageData | null {
    const cov = this.src.serpRivalCoverage;
    // A failed comparison stored as `ready` (§0au) has no sections because nothing was compared.
    if (!cov || effectiveSectionCompareStatus(cov) !== 'ready') return null;
    const rival = this.topEditorialSerpRival();
    if (!rival || normalizeUrlForCompare(cov.rivalUrl) !== normalizeUrlForCompare(rival.url)) {
      return null;
    }
    const query = this.src.pageSerp?.queryDisplay?.trim();
    if (query && cov.query?.trim() && query !== cov.query.trim()) return null;
    return cov;
  }

  /** The Top SERP rival's eval row, when its section coverage is ready. */
  serpRivalEvalRow(): SupportingPagesV2RowResult | null {
    const cov = this.activeSerpRivalCoverage();
    if (!cov?.evalRow) return null;
    return cov.evalRow as SupportingPagesV2RowResult;
  }

  /**
   * V2 rows for the section-compare UI, augmented with the precomputed Top SERP
   * rival row (so "what they cover that your page doesn't" renders it even when
   * the rival's domain is not a marked competitor / has no v1 snapshot).
   */
  mergedV2Rows(): SupportingPagesV2RowResult[] {
    const base = this.src.topicOwnershipV2Data?.rows ? [...this.src.topicOwnershipV2Data.rows] : [];
    const rival = this.serpRivalEvalRow();
    if (rival) {
      const key = normalizeUrlForCompare(rival.competitorUrl);
      if (!base.some((r) => normalizeUrlForCompare(r.competitorUrl) === key)) {
        base.push(rival);
      }
    }
    return base;
  }

  topicOwnershipPagePath(pageUrl: string): string {
    return formatSupportingPagePath(pageUrl);
  }

  topicOwnershipPageTraffic(traffic: number | undefined): string {
    return formatSupportingPageTraffic(traffic);
  }

  /** Rows tagged with focal anchor topics (hides weak secondary-topic pages from diagnostics). */
  getTopicOwnershipRowsForDisplay(block: TopicOwnershipDomain): DomainTopicUrlRow[] {
    if (!this.src.focalAnchorTopicSlugs.size) {
      return block.rows;
    }
    return filterRowsByFocalAnchorTopics(block.rows, this.src.focalAnchorTopicSlugs);
  }

  extractDomainFromUrl(url: string): string {
    try {
      const parsed = new URL(url);
      return parsed.hostname.toLowerCase().replace(/^www\./, '');
    } catch {
      return '';
    }
  }

  /**
   * The Missing sections card's rows, before the Issues queue says whether the
   * card is still open. Null when there is nothing to show — the card's own
   * rule, which the component's work-item builder used to hold inline.
   */
  reviewSectionGapContent(): { missing: ReviewMissingSectionsView | null; rank: ReviewRankGapsView | null } | null {
    if (!this.src.embedReviewSurface) return null;
    if (!(this.src.pageUrlId || '').trim() || !(this.src.url || '').trim()) return null;
    const missing = this.reviewMissingSections();
    const rank = this.reviewRankGaps();
    if (!missing && !rank) return null;
    return { missing, rank };
  }

  /**
   * Why the section comparison is blank, when it is blank for a reason (§0at).
   *
   * Review surface only. Inventory and URL detail show the whole compare
   * apparatus, including a Fetch control for exactly this state — there, a
   * sentence saying we could not read the rival sits above the thing that
   * fixes it. On the belt there is no such control and no such context, so a
   * card that simply does not appear reads as "we found nothing wrong", which
   * on the largest-gap page of a domain is the opposite of the truth.
   *
   * `sectionCardShown` is passed in because the belt also keeps the card up
   * while its send animation plays, which only the component knows about.
   */
  reviewSectionCompareNotice(sectionCardShown: boolean): SectionCompareNotice | null {
    if (!this.src.embedReviewSurface) return null;
    // Held back for a bigger reason, which the held-back card states (§0ar), or results that show
    // another page of the site: the rival is that page's, not this one's. Only then: a page with
    // nothing held back keeps this notice rather than going blank.
    if (this.reviewComparisonWithheld() || this.reviewComparisonOnAnotherPage()) return null;
    const cov = this.src.serpRivalCoverage;
    if (!cov) return null;
    // A comparison we DID manage to show is never explained away.
    if (sectionCardShown) return null;
    return sectionCompareNotice(effectiveSectionCompareStatus(cov), cov.rivalDomain);
  }

  /**
   * Whether the Content section renders at all — the frame both content cards,
   * and the blank-comparison notice, sit in.
   *
   * Two cards live under this heading on review now. A page whose only content
   * finding is a missing section still has content work, and gating the whole
   * section on the pages card would hide it. `sectionCardShown` is the Missing
   * sections card, which only the component can answer for while its send
   * animation plays.
   *
   * Either notice opens the section on its own: without the frame it had nowhere to render, and a
   * page whose comparison failed (§0aj, §0au) or whose rival we could not read (§0aq) read exactly
   * like a page that was compared and had nothing missing. The unreadable rival only showed on a
   * page with other content work: on the sprint 6 queues, 2 of its 19 pages.
   */
  showEmbedContentSection(sectionCardShown: boolean): boolean {
    if (!this.isPlanV2Embed()) return true;
    if (!this.embedActionsOnlyActive()) return true;
    return (
      this.showEmbedSupportingPagesCard() ||
      sectionCardShown ||
      this.reviewSectionCompareNotice(sectionCardShown) !== null
    );
  }

  /**
   * Links to add pointing at this page, as the Internal links card lists them:
   * open, incoming, not already sent to Work items — and none while the card is
   * no longer open in the Issues queue.
   */
  reviewInternalLinkRows(): ActionPlanInternalLinkRow[] {
    if (!this.isPlanV2Embed() || this.src.pageSignalsLoading) return [];
    if (!(this.src.pageUrlId || '').trim() || !(this.src.url || '').trim()) return [];
    if (!this.src.isIssueVisible(this.planV2IssueKey('internal_link'))) return [];
    return this.pageSignalInternalLinkDetailsPendingTriage();
  }

  /**
   * Marked rivals on this page's SERP that no stored evaluation covers yet.
   *
   * The belt evaluates these live (`ensureMarkedSerpCompetitorEvals`); a reader
   * that wants the card as the belt shows it has to evaluate the same ones.
   */
  missingMarkedSerpSeeds(): SerpCompetitorColumnSeed[] {
    const seeds = this.supportingPagesSerpSeeds();
    if (!seeds.length) return [];
    const existing = new Set(this.allMarkedV2Rows().map((r) => normalizeUrlForCompare(r.competitorUrl)));
    return seeds.filter((s) => !existing.has(normalizeUrlForCompare(s.url)));
  }

  private reviewTechnicalFindings(views: OnpageIssueView[]): ReviewTechnicalFinding[] {
    return this.reviewTechnicalIssues(views).map((issue) => ({
      code: issue.code,
      title: issue.title,
      scope: this.technicalIssueScopeLabel(issue)
    }));
  }

  private reviewAlreadyOnPage(): ReviewPageCards['alreadyOnPage'] {
    const sections = this.src.isIssueVisible(this.planV2IssueKey('section_gap')) ? this.reviewSectionsAlreadyOnPage() : [];
    const newPages = this.src.isIssueVisible(this.planV2IssueKey('topic_ownership'))
      ? this.reviewNewPageIdeasAlreadyOnPage()
      : [];
    return sections.length || newPages.length ? { sections, newPages } : null;
  }

  /**
   * This page's model as another surface embeds it: the same data, read live, under other embed
   * flags. Nothing is copied, so a load that lands later is seen by both.
   */
  onSurface(
    flags: Partial<Pick<ReviewPageSource, 'embedPreset' | 'embedActionsOnly' | 'embedReviewSurface' | 'embedCompactPeek'>>
  ): ReviewPageModel {
    const props: PropertyDescriptorMap = {};
    for (const [key, value] of Object.entries(flags)) props[key] = { value, enumerable: true };
    return new ReviewPageModel(Object.create(this.src, props) as ReviewPageSource);
  }

  /**
   * The cards `/review` shows for this page, asked from whichever surface this model serves. The
   * Dashboard's compact peek reads them so its row says what the belt says (TECHNICAL_DEBT §0ck);
   * its own flags would drop the technical cards, which the peek never rendered as cards. The flags
   * are the belt's (`review-page-build.ts`, `startReviewPageSource`).
   */
  reviewSurfaceCards(): ReviewPageCards {
    return this.onSurface({
      embedPreset: 'planV2',
      embedActionsOnly: true,
      embedReviewSurface: true,
      embedCompactPeek: false
    }).reviewPageCards();
  }

  /**
   * Every card the review belt renders for this page, as data — see
   * `ReviewPageCards`. Built only from the rules above, so it cannot list
   * something the belt does not show.
   */
  reviewPageCards(): ReviewPageCards {
    const hasPage = Boolean((this.src.pageUrlId || '').trim() && (this.src.url || '').trim());
    const sectionContent = this.reviewSectionGapContent();
    const sectionsShown =
      !!sectionContent && this.src.isIssueVisible(this.planV2IssueKey('section_gap'));
    const answer = hasPage && this.showEmbedSerpPhraseCard() ? this.reviewAnswerStructure() : null;
    const d = this.src.pageIntentDiagnosis;
    const links = this.reviewInternalLinkRows();
    const unclustered = this.pageSignalUnclusteredDetail();

    return {
      supportingPages:
        hasPage && this.showEmbedSupportingPagesCard() ? this.reviewSupportingPagesColumns() : null,
      sections: sectionsShown
        ? { title: this.reviewGapsCardTitle(), missing: sectionContent.missing, rank: sectionContent.rank }
        : null,
      sectionCompareNotice: this.showEmbedContentSection(sectionsShown)
        ? this.reviewSectionCompareNotice(sectionsShown)
        : null,
      alreadyOnPage: hasPage ? this.reviewAlreadyOnPage() : null,
      comparisonWithheld: hasPage ? this.reviewComparisonWithheld() : null,
      comparisonOnAnotherPage: hasPage ? this.reviewComparisonOnAnotherPage() : null,
      freshness:
        hasPage && this.showEmbedFreshnessSection()
          ? {
              label: this.freshnessDiagnosisLabel(),
              tone: this.freshnessDiagnosisTone(),
              yourPage: this.pageContentLastModifiedLabel(),
              serpTypical: this.serpWinnersPublishedDateSummary(),
              verdict: this.contentFreshnessVerdict()
            }
          : null,
      answerStructure: answer
        ? {
            instruction: answer.instruction,
            vocab: answer.vocab,
            yours: answer.yours,
            theirs: answer.theirs.map((t) => ({
              position: t.position,
              domain: t.domain,
              text: t.segments.map((seg) => seg.text).join('')
            }))
          }
        : null,
      pageType:
        hasPage && d && this.showEmbedSerpIntentCard()
          ? {
              chipLabel: this.pageIntentMatchActionChipLabel(),
              serpFamily: this.pageIntentFamilyLabel(d.serpFamily),
              yourFamily: this.pageIntentYourPageFamilyLabel(),
              briefLines: this.pageTypeBriefLines(),
              examplesLabel: this.pageTypeExamplesLabel(),
              examples: this.pageTypeExamples()
            }
          : null,
      technicalIssues:
        hasPage && this.showEmbedTechnicalIssuesCard()
          ? this.reviewTechnicalFindings(this.technicalCriticalIssueViews())
          : null,
      technicalWarnings:
        hasPage && this.showEmbedTechnicalWarningsCard()
          ? this.reviewTechnicalFindings(this.technicalWarningIssueViews())
          : null,
      internalLinks: links.length ? links : null,
      cannibalization:
        hasPage && this.showEmbedCannibalizationSection() ? { pairs: this.reviewCannibalizationPairs() } : null,
      nearMissSearches: hasPage && this.showEmbedNearMissSection() ? this.reviewNearMissCard() : null,
      signals:
        hasPage && this.showEmbedSignalsSection() && this.hasNonInternalLinkSignals()
          ? {
              labels: this.reviewPageSignals().map((sig) => sig.label),
              planClusters: [...this.src.planClusters],
              unclustered: unclustered
                ? {
                    recommendation: unclustered.recommendation,
                    label: this.unclusteredActionLabel(unclustered),
                    evidence: this.unclusteredEvidenceSentence(unclustered),
                    clusterFit: this.unclusteredClusterFitSentence(unclustered),
                    topTopics: unclustered.topTopics ?? []
                  }
                : null,
              noPlanCluster: this.hasPageSignalKind('no_cluster')
            }
          : null,
      estimateBesideRival: hasPage ? this.reviewEstimateBesideRival() : null
    };
  }
}

/** One page however its URL is spelled: scheme, `www.` and a trailing slash aside. */
function sameUrl(a: string, b: string): boolean {
  const key = (u: string) =>
    u
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '');
  return key(a) === key(b);
}
